import { queueMacrotask } from 'module/helpers/macrotask';
import { useTranslation } from 'module/hooks/use-translation';
import { EditorPopoverState } from 'module/ui/components/atoms/rich-text-editor/editor-popover/types';
import { InputField } from '@rocketlane/ui';
import { Editor, posToDOMRect } from '@tiptap/core';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import {
  CommandPickerItem,
  CommandPickerLeafItem,
} from '../../command-picker/types';
import {
  CommandsPickerPopover,
  CommandPickerPopoverRef,
} from '../../command-picker/ui/picker';

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  padding: var(--spacing-03) 0;
  width: 100%;

  .rl-input-wrapper {
    width: 100%;
  }

  .rl-input {
    background-color: var(--blue-10);
    border-color: var(--blue-30);
    width: 100%;
    outline-color: var(--blue-30);

    &:focus {
      outline-color: var(--blue-30);
    }
    &:hover {
      border-color: var(--blue-30);
    }
  }
`;

interface Props {
  position: number;
  editor: Editor;
  items:
    | CommandPickerItem[]
    | ((editor: Editor) => Promise<CommandPickerItem[]>);
  afterRemovePlaceholder: VoidFunction;
}

export const CommandPickerWithInput = ({
  position,
  editor,
  items,
  afterRemovePlaceholder,
}: Props) => {
  const removePlaceholder = useCallback(() => {
    editor.chain().focus().removeCommandTriggerPlaceholder(position).run();
    afterRemovePlaceholder();
  }, [editor, position, afterRemovePlaceholder]);
  const positionRef = useRef(position);
  const rectRef = useRef<DOMRect | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const getPosition = useCallback(() => {
    // no need to recalculate the position if it hasn't changed
    if (positionRef.current === position && rectRef.current != null) {
      return rectRef.current;
    }
    positionRef.current = position;
    const inputRect = inputRef.current?.getBoundingClientRect();

    if (inputRect) {
      rectRef.current = inputRect;
    } else {
      const node = editor.view.nodeDOM(position) as HTMLElement;

      if (node) {
        rectRef.current = node.getBoundingClientRect();
      } else {
        rectRef.current = posToDOMRect(editor.view, position, position);
      }
    }

    return rectRef.current;
  }, [editor, position]);

  const popoverState: EditorPopoverState = useMemo(() => {
    return {
      visible: true,
      position: getPosition(),
    };
  }, [getPosition]);

  const [query, setQuery] = useState('');
  const commandPickerRef = useRef<CommandPickerPopoverRef>(null);
  const suggestionProps = useMemo(() => {
    return {
      editor,
      range: { from: position, to: position },
      query,
      text: query,
      items: [],
      command: (item: CommandPickerLeafItem) => {
        removePlaceholder();
        queueMacrotask(() => {
          item.execute(editor, { from: position, to: position });
        });
      },
      decorationNode: null,
      clientRect: getPosition,
    };
  }, [editor, getPosition, position, query, removePlaceholder]);

  useEffect(() => {
    commandPickerRef.current?.update(suggestionProps);
  }, [suggestionProps]);

  const onKeyDown = useCallback(
    (event) => {
      if (event.key === 'Escape') {
        removePlaceholder();

        return;
      }
      queueMacrotask(() => {
        commandPickerRef.current?.onKeyDown({
          event,
          range: { from: position, to: position },
          view: editor.view,
        });
      });
    },
    [removePlaceholder, editor.view, position],
  );
  const onInputChange = useCallback((event) => {
    setQuery(event.target.value);
  }, []);

  useEffect(() => {
    if (inputRef.current) {
      queueMacrotask(() => {
        inputRef.current?.focus();
      });
    }
  }, []);

  return (
    <Wrapper>
      <InputField
        autoFocus
        ref={inputRef}
        placeholder={useTranslation(
          'rich_text_editor.sections.add_block.input.placeholder',
        )}
        onKeyDown={onKeyDown}
        onChange={onInputChange}
        className="command-picker-input"
      />
      <CommandsPickerPopover
        editor={editor}
        popoverState={popoverState}
        suggestionProps={suggestionProps}
        ref={commandPickerRef}
        items={items}
        popoverContentContainerClassName="command-picker-with-input"
        canIgnoreOutsideClickFor={(target) =>
          target.closest('.command-picker-with-input') !== null ||
          target.closest('.command-picker-input') !== null
        }
        hidePopover={() => {
          removePlaceholder();
        }}
      />
    </Wrapper>
  );
};
